import {
  ThemeBreakpointsSchema,
  ThemeButtonVariantsSchema,
  ThemeColorKey,
  ThemeColorsSchema,
  ThemeFontFamilyItemSchema,
  ThemeFontFamilySchema,
  ThemeProps,
  ThemeShadowSchema,
  ThemeTextVariantsSchema,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { FONT_WEIGHT_MAP } from '@mutiny-pkg/tailwind-parser';
import { LexicalThemeColors } from '../../../richtext/types';
import { parameterize } from '@mutiny-pkg/formatters';
import z from 'zod';

export const TAILWIND_CSS_CLASS = 'mutiny-tw';

export interface PageProps {
  children: React.ReactNode;
}

export type FontFaceType = {
  fontFamily: string;
  fontWeight: string;
  src: string;
  fontStyle: string;
};

export const getFontFamilies = (themeProps: ThemeProps) => {
  const fonts: z.infer<typeof ThemeFontFamilyItemSchema>[] = [];
  const fontFamilyTheme = ThemeFontFamilySchema.strip().parse(themeProps);
  fontFamilyTheme.fonts.forEach((font) => {
    if (font.family) {
      fonts.push(font);
    }
  });
  return fonts;
};

// Font loading function similar to useFonts.ts
export const loadFontFace = async (fontFamily: string, fontWeight: string, src: string, style?: string) => {
  if (!fontFamily || !fontWeight || !src) {
    return;
  }

  const fontFace = new FontFace(fontFamily, `url(${src})`, { weight: fontWeight, style: style || 'normal' });
  document.fonts.add(fontFace);
  try {
    await fontFace.load();
  } catch (error) {
    console.warn('Error loading font face', error);
  }
};

// Process theme font families into loadable font faces
export const processFontFamily = (fontFamilies?: z.infer<typeof ThemeFontFamilyItemSchema>[]): FontFaceType[] => {
  if (!fontFamilies || !Array.isArray(fontFamilies)) return [];

  const fontFaces: FontFaceType[] = [];

  fontFamilies.forEach((family) => {
    family.variants?.forEach((variant) => {
      if (variant.url) {
        fontFaces.push({
          fontFamily: family.family,
          fontWeight: FONT_WEIGHT_MAP[variant.weight] || variant.weight,
          src: variant.url,
          fontStyle: variant.style,
        });
      }
    });
  });

  return fontFaces;
};

// Generate Tailwind CSS variable overrides from flattened theme keys
export const generateTailwindVariables = (theme: ThemeProps): Record<string, string> => {
  const variables: Record<string, string> = {};

  // Font families
  const fontFamilyTheme = ThemeFontFamilySchema.strip().parse(theme);
  (Object.keys(fontFamilyTheme) as Array<keyof typeof fontFamilyTheme>).forEach((key) => {
    fontFamilyTheme[key].forEach((font) => {
      if (font.family) {
        variables[`--font-${font.name}`] = font.family;
      }
    });
  });

  // Colors - override Tailwind's built-in color system
  const baseColorsTheme = ThemeColorsSchema.strip().parse(theme);
  (Object.keys(baseColorsTheme) as Array<keyof typeof baseColorsTheme>).forEach((key) => {
    if (key === 'colors.additional') {
      baseColorsTheme['colors.additional'].forEach(({ name, color }) => {
        const identifier = parameterize(name);
        variables[`--color-${identifier}`] = color;
      });
    } else {
      const value = baseColorsTheme[key];
      const colorKey = key.replace('colors.', '');
      if (value?.color) {
        variables[`--color-${colorKey}`] = value.color;
      }
    }
  });

  // Shadows
  const shadowTheme = ThemeShadowSchema.strip().parse(theme);
  (Object.keys(shadowTheme) as Array<keyof typeof shadowTheme>).forEach((key) => {
    const value = shadowTheme[key];
    const size = key.replace('shadow.', '');
    if (value) {
      variables[`--shadow-${size}`] = value;
    }
  });

  // Breakpoints
  const breakpointsTheme = ThemeBreakpointsSchema.strip().parse(theme);
  (Object.keys(breakpointsTheme) as Array<keyof typeof breakpointsTheme>).forEach((key) => {
    const value = breakpointsTheme[key];
    const size = key.replace('breakpoints.', '');
    if (value) {
      variables[`--breakpoint-${size}`] = value;
    }
  });

  return variables;
};

// Generate @layer components CSS for all variant definitions
export const generateVariantComponents = (theme: ThemeProps): string => {
  const components: string[] = [`.${TAILWIND_CSS_CLASS} { @apply text-dark; }`];

  // Process button variants
  const buttonVariantsTheme = ThemeButtonVariantsSchema.strip().parse(theme);
  (Object.keys(buttonVariantsTheme) as Array<keyof typeof buttonVariantsTheme>).forEach((key) => {
    const variantName = key.replace('variants.button.', '');
    const variant = buttonVariantsTheme[key];

    if (variant) {
      // Generate base class
      if (variant.base) {
        const utilities = Object.values(variant.base).filter((value) => value !== undefined);
        if (utilities.length) {
          components.push(`.${TAILWIND_CSS_CLASS} .btn-${variantName} { @apply ${utilities.join(' ')} }`);
        }
      }

      // Generate hover class
      if (variant.hover) {
        const utilities = Object.values(variant.hover).filter((value) => value !== undefined);
        if (utilities.length) {
          components.push(`.${TAILWIND_CSS_CLASS} .btn-${variantName}:hover { @apply ${utilities.join(' ')} }`);
        }
      }
    }
  });

  // Process text variants
  const textVariantsTheme = ThemeTextVariantsSchema.strip().parse(theme);
  (Object.keys(textVariantsTheme) as Array<keyof typeof textVariantsTheme>).forEach((key) => {
    const variantName = key.replace('variants.text.', '');
    const variant = textVariantsTheme[key];

    if (variant) {
      // Generate base class with all non-hover properties
      const utilities = Object.values(variant).filter((value) => value !== undefined);
      if (utilities.length) {
        components.push(`.${TAILWIND_CSS_CLASS} .text-${variantName} { @apply ${utilities.join(' ')} }`);
      }
    }
  });

  return `@layer components {\n    ${components.join('\n    ')}\n  }`;
};

export type ThemeColor = {
  label: string;
  value: string;
  colorValue: string;
  tokenName?: string;
};

/**
 * Checks if a color is a named theme color. This check will match color strings like `primary` or `secondary`
 * but not `#000000` or `#FFFFFF` which are either custom colors or additional colors from the theme.
 */
export const isNamedThemeColor = (color: string, colors: ThemeColor[]): boolean => {
  return colors.some((c) => c.tokenName === color);
};

export const getColorsFromTheme = (theme: ThemeProps): ThemeColor[] => {
  const colorsTheme = ThemeColorsSchema.strip().parse(theme);
  const colors: ThemeColor[] = [];

  // Keys that should be excluded from the output if not present in the original theme
  const optionalColorKeys = new Set([ThemeColorKey.Secondary, ThemeColorKey.Tertiary]);

  (Object.keys(colorsTheme) as Array<keyof typeof colorsTheme>).forEach((key) => {
    if (key === 'colors.additional') {
      colorsTheme['colors.additional'].forEach(({ name, color }) => {
        const identifier = parameterize(name);
        colors.push({
          label: name,
          value: identifier,
          colorValue: color,
        });
      });
    } else {
      // Skip optional colors that don't exist in the original theme
      if (optionalColorKeys.has(key as ThemeColorKey) && theme[key] === undefined) {
        return;
      }
      const colorKey = key.replace('colors.', '');
      colors.push({
        label: colorsTheme[key].name,
        value: colorKey,
        colorValue: colorsTheme[key].color,
        tokenName: colorKey,
      });
    }
  });
  return colors;
};

export const getLexicalThemeColors = (theme: ThemeProps): LexicalThemeColors => {
  const colors = getColorsFromTheme(theme);

  // Support both old schema (textDark/textLight) and new schema (dark/light)
  // Try new schema first, fall back to old schema for backwards compatibility
  const textDarkColor =
    colors.find((c) => c.tokenName === 'dark')?.colorValue ??
    colors.find((c) => c.tokenName === 'textDark')?.colorValue ??
    '';

  const textLightColor =
    colors.find((c) => c.tokenName === 'light')?.colorValue ??
    colors.find((c) => c.tokenName === 'textLight')?.colorValue ??
    '';

  return {
    color: {
      primary: colors.find((c) => c.tokenName === 'primary')?.colorValue ?? '',
      secondary: colors.find((c) => c.tokenName === 'secondary')?.colorValue ?? '',
      tertiary: colors.find((c) => c.tokenName === 'tertiary')?.colorValue ?? '',
      default: colors.find((c) => c.tokenName === 'default')?.colorValue ?? '',
      textDark: textDarkColor,
      textLight: textLightColor,
      none: '',
      unset: '',
    },
    additionalColors: colors.filter((c) => c.tokenName === undefined).map((c) => c.colorValue),
  };
};
